import { useState } from 'react'
import { Gavel, Pencil, Plus } from 'lucide-react'
import { write } from '../api'
import { dateTime, words } from '../format'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Notice,
  Panel,
  ReasonField,
  Refusal,
} from '../ui'

// ======================================================================
// FINDINGS
// ======================================================================
//
// NOTHING HERE SUGGESTS, PRE-FILLS, SCORES OR CHARACTERISES A FINDING.
// `finding` and `reasoning` are free text because a person writes them —
// migration 054 says why: "the reasoning a decision-maker writes is the
// defensible artefact, and a dropdown would flatten it." There is no
// outcome picklist in this file, no suggested wording, no template and
// no strength indicator, and none may be added.
//
// THE STAGE IS NEVER SENT. er_cases/findings.py derives it from the
// recorder's own role, and migration 053's
// uq_er_party_one_adjudicating_role guarantees that derivation has
// exactly one answer. A stage field in the request would let a decision
// maker file the appeal outcome on their own decision.
//
// THE APPEAL PATH IS NOT BUILT. This surface records findings at the
// original stage only. Recording at appeal happens after a case has
// closed, and the backend refuses findings on a closed case, so that
// path does not work yet — building UI for it would be building a
// control that cannot succeed. Findings that ALREADY carry stage
// "appeal" are displayed, because they are part of the record; there is
// simply no control here that creates one.
//
// REASONING IS OPTIONAL ON THE WIRE AND PRESSED FOR HERE. A finding with
// no reasoning is accepted by the system and is close to indefensible at
// a tribunal. schemas.py names this and asks the UI to press without
// turning it into a validation rule — so the field is prominent, the
// warning fires on an empty save, and the save still goes through if the
// person means it.

export default function Findings({
  caseId,
  items,
  allegations,
  capabilities,
  caseStatus,
  me,
  onReplace,
}) {
  const [dialog, setDialog] = useState(null)
  const canRecord =
    capabilities.includes('record_finding') && caseStatus !== 'closed'

  const byAllegation = new Map()
  items.forEach((f) => {
    const list = byAllegation.get(f.allegation_id) || []
    list.push(f)
    byAllegation.set(f.allegation_id, list)
  })

  // Only allegations that have actually been put to the subject can
  // carry a finding — a decision reached on something they were never
  // told about is unfair in a way the finished report will not show.
  // The guard lives in the service; this only avoids offering a control
  // that exists to be refused.
  const open = (allegations || []).filter(
    (a) =>
      a.disclosed_at &&
      a.status !== 'not_pursued' &&
      !(byAllegation.get(a.allegation_id) || []).some((f) => f.stage === 'original'),
  )

  return (
    <Panel
      eyebrow="Decisions"
      title="Findings"
      right={
        canRecord &&
        open.length > 0 && (
          <Button size="sm" icon={Plus} onClick={() => setDialog({ kind: 'record' })}>
            Record a finding
          </Button>
        )
      }
    >
      {items.length === 0 ? (
        <Empty icon={Gavel} title="No findings have been recorded">
          {canRecord
            ? 'A finding is recorded against one allegation at a time, and only against allegations that have been put to the subject.'
            : null}
        </Empty>
      ) : (
        <ul className="space-y-2.5">
          {items.map((f) => {
            const mine = me && f.recorded_by === me
            return (
              <li key={f.finding_id} className="panel-inset px-4 py-3.5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="mono text-accent">
                        allegation #{f.allegation_ordinal}
                      </span>
                      <Chip tone={f.stage === 'appeal' ? 'blue' : 'accent'}>
                        {words(f.stage)}
                      </Chip>
                    </div>

                    <p className="measure mt-2 whitespace-pre-wrap font-head text-[13.5px] font-semibold leading-snug">
                      {f.finding}
                    </p>

                    {f.reasoning ? (
                      <p className="measure mt-2 whitespace-pre-wrap text-[12.5px] leading-relaxed text-muted">
                        {f.reasoning}
                      </p>
                    ) : (
                      <p className="mt-2 text-[12px] text-amber">
                        No reasoning was recorded with this finding.
                      </p>
                    )}

                    <div className="mt-2 text-[11.5px] text-faint">
                      {f.recorded_by_label && <>Recorded by {f.recorded_by_label} · </>}
                      {dateTime(f.recorded_at)}
                    </div>
                  </div>

                  {/* Amendable only by the person who recorded it, at
                      their own stage. An appeal manager records an
                      appeal finding rather than editing the original. */}
                  {mine && f.stage === 'original' && caseStatus !== 'closed' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={Pencil}
                      onClick={() => setDialog({ kind: 'amend', finding: f })}
                    >
                      Amend
                    </Button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {dialog?.kind === 'record' && (
        <RecordDialog
          caseId={caseId}
          allegations={open}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'amend' && (
        <AmendDialog
          caseId={caseId}
          finding={dialog.finding}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
    </Panel>
  )
}

function RecordDialog({ caseId, allegations, onClose, onReplace }) {
  const [allegationId, setAllegationId] = useState(allegations[0]?.allegation_id ?? '')
  const [finding, setFinding] = useState('')
  const [reasoning, setReasoning] = useState('')
  const [confirmedEmpty, setConfirmedEmpty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const needsReasoningPrompt = !reasoning.trim() && !confirmedEmpty

  return (
    <Modal
      open
      onClose={onClose}
      title="Record a finding"
      subtitle="On one allegation. Nothing in this system suggests, scores or characterises what you write — the words are yours and they are what will be relied on."
      width="max-w-2xl"
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <div className="space-y-4">
        <Field label="Which allegation" required>
          <select
            className="field"
            value={allegationId}
            onChange={(e) => setAllegationId(e.target.value)}
          >
            {allegations.map((a) => (
              <option key={a.allegation_id} value={a.allegation_id}>
                #{a.ordinal} — {a.body.slice(0, 80)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="The finding" required>
          <textarea
            className="field"
            rows={3}
            autoFocus
            value={finding}
            onChange={(e) => setFinding(e.target.value)}
          />
        </Field>

        <Field
          label="Your reasoning"
          hint="Why you reached it, on the material in front of you. This is the part that is relied on if the decision is challenged."
        >
          <textarea
            className="field field-reason"
            rows={8}
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
          />
        </Field>
      </div>

      {needsReasoningPrompt && finding.trim() && (
        <div className="mt-4">
          <Notice tone="amber" title="You have not written any reasoning">
            <p>
              The system will accept this. A finding with no reasoning is very
              hard to defend later — the finding says what was decided, and
              nothing says why.
            </p>
            <label className="mt-2 flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                className="accent-[color:var(--accent)]"
                checked={confirmedEmpty}
                onChange={(e) => setConfirmedEmpty(e.target.checked)}
              />
              <span className="text-[12px] text-ink">
                Record it without reasoning anyway
              </span>
            </label>
          </Notice>
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!finding.trim() || !allegationId || needsReasoningPrompt}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onReplace(
                await write(`/${caseId}/allegations/${allegationId}/finding`, {
                  body: { finding, reasoning: reasoning.trim() || null },
                }),
              )
              onClose()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Record the finding
        </Button>
      </div>
    </Modal>
  )
}

/** THE ROW IS UPDATED IN PLACE, so the audit chain is the only surviving
 *  record of the previous text. That is why a reason is required, and
 *  why this dialog says so. */
function AmendDialog({ caseId, finding: original, onClose, onReplace }) {
  const [finding, setFinding] = useState(original.finding)
  const [reasoning, setReasoning] = useState(original.reasoning || '')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  return (
    <Modal
      open
      onClose={onClose}
      title={`Amend your finding on allegation #${original.allegation_ordinal}`}
      width="max-w-2xl"
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <div className="mb-4">
        <Notice tone="amber" title="This replaces the text rather than adding to it">
          The finding row is changed in place. The previous wording survives only
          in the audit record, which is why the reason below is required.
        </Notice>
      </div>

      <ReasonField
        value={reason}
        onChange={setReason}
        label="Why you are amending it"
        hint="Recorded in the audit trail alongside the text you are replacing."
      />

      <div className="mt-4 space-y-4">
        <Field label="The finding" required>
          <textarea
            className="field"
            rows={3}
            value={finding}
            onChange={(e) => setFinding(e.target.value)}
          />
        </Field>
        <Field label="Your reasoning">
          <textarea
            className="field"
            rows={8}
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!finding.trim() || !reason.trim()}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onReplace(
                await write(`/${caseId}/findings/${original.finding_id}`, {
                  method: 'PATCH',
                  body: { finding, reasoning: reasoning.trim() || null, reason },
                }),
              )
              onClose()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Save the amendment
        </Button>
      </div>
    </Modal>
  )
}
